import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Badge from '@material-ui/core/Badge';
import Tooltip from '@material-ui/core/Tooltip';
import { request } from 'utilities/graph';
import { getVideosByTypeByCreatedAt } from 'graphql/queries';
import cache from 'utilities/cache';
import { useLocation } from 'react-router-dom';
import { NOTIFICATION_CATEGORIES, NEW_VIDEO_UPDATED_EVENT } from '../constants/videoNotification';

export default function VideoBadge({ children }) {
  const location = useLocation();
  const isInInstructionCenter = location.pathname.includes('instructionCenter');
  const isInInstructionVideo = location.pathname.includes('instructionVideo');
  const [hasNewVideo, setHasNewVideo] = useState(false);
  const lastUpdateTimeRef = useRef(null);

  const shouldCheckNewVideo = () => {
    // 如果在教學中心，強制檢查
    if (isInInstructionCenter || isInInstructionVideo) {
      return true;
    }

    // 其他情況檢查10分鐘限制
    const now = Date.now();
    if (!lastUpdateTimeRef.current) return true;

    const timeSinceLastUpdate = now - lastUpdateTimeRef.current;
    const TEN_MINUTES = 10 * 60 * 1000;

    return timeSinceLastUpdate >= TEN_MINUTES;
  };

  const checkNewVideo = async () => {
    lastUpdateTimeRef.current = Date.now();

    try {
      const { data: { getVideosByTypeByCreatedAt: { items: videos } } } = await request(getVideosByTypeByCreatedAt, {
        type: 'Video',
        limit: 1,
        sortDirection: 'DESC',
      });

      if (videos.length === 0) return;

      const latestVideo = videos[0];
      const notifiedTime = cache.get('app:facility:notifiedNewestVideoTime');

      // 只有當 category 是 'new' 或 'update' 時才顯示紅點
      const shouldShowBadge = NOTIFICATION_CATEGORIES.includes(latestVideo.category) &&
        (!notifiedTime || new Date(latestVideo.createdAt) > new Date(notifiedTime));

      setHasNewVideo(shouldShowBadge);
    } catch (error) {
      console.error('Error checking for new videos:', error);
    }
  };

  useEffect(() => {
    if (shouldCheckNewVideo()) {
      checkNewVideo();
    }
  }, [location.pathname]);

  useEffect(() => {
    // 監聽 cache 更新事件
    window.addEventListener(NEW_VIDEO_UPDATED_EVENT, checkNewVideo);

    return () => {
      window.removeEventListener(NEW_VIDEO_UPDATED_EVENT, checkNewVideo);
    };
  }, []);

  return (
    <Tooltip
      title={hasNewVideo ? '有新的教學影片' : '沒有新的教學影片'}
      placement="right"
    >
      <Badge
        variant="dot"
        color="secondary"
        invisible={!hasNewVideo}
      >
        {children}
      </Badge>
    </Tooltip>
  );
}

VideoBadge.propTypes = {
  children: PropTypes.node,
};
